/**
 * Compartir por link temporal (F5): expiración, un solo uso, TOTP (solo owner).
 * Muestra el link creado para copiar y los links vigentes del secreto (con IP y
 * vistas), con revocación.
 */
import { useState } from 'react';
import { Copy, Link2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { errMsg, isVaultLockedError, useCopy, withUnlockRetry } from '@/hooks/useVault';
import { fmtRelative } from './vault-ui';
import type { RouterOutputs } from '@/lib/api-types';
import { VAULT_POLICY } from '../../../../shared/types/vault';

interface ShareLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  secretId: string;
  canOwn: boolean;
  hasTotp: boolean;
  ensureUnlocked: () => Promise<boolean>;
}

type LinkStatus = RouterOutputs['vault']['listSecretLinks'][number]['status'];
const STATUS_VARIANT: Record<LinkStatus, 'success' | 'secondary' | 'outline'> = { active: 'success', dormant: 'outline', expired: 'secondary', consumed: 'secondary', revoked: 'outline' };

export function ShareLinkDialog({ open, onOpenChange, secretId, canOwn, hasTotp, ensureUnlocked }: ShareLinkDialogProps) {
  const { t, i18n } = useTranslation();
  const copy = useCopy();
  const utils = trpc.useUtils();
  const links = trpc.vault.listSecretLinks.useQuery({ secretId }, { enabled: open, staleTime: 15_000 });
  const [hours, setHours] = useState('24');
  const [oneTime, setOneTime] = useState(true);
  const [includeTotp, setIncludeTotp] = useState(false);
  const [created, setCreated] = useState<{ url: string; expiresAt: string } | null>(null);

  const create = trpc.vault.createShareLink.useMutation({
    onSuccess: (r) => {
      setCreated({ url: r.url, expiresAt: r.expiresAt });
      utils.vault.listSecretLinks.invalidate({ secretId });
      utils.vault.getSecret.invalidate({ secretId });
    },
    onError: (e) => { if (!isVaultLockedError(e)) toast.error(errMsg(e)); },
  });
  const revoke = trpc.vault.revokeShareLink.useMutation({
    onSuccess: () => {
      utils.vault.listSecretLinks.invalidate({ secretId });
      toast.success(t('vault.share.revoked', 'Link revocado'));
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const submit = async () => {
    await withUnlockRetry(ensureUnlocked, () => create.mutateAsync({ secretId, expiresInHours: Number(hours), oneTime, includeTotp: includeTotp && canOwn && hasTotp })).catch(() => undefined);
  };

  const statusLabel: Record<LinkStatus, string> = {
    active: t('vault.share.st_active', 'Activo'),
    dormant: t('vault.share.st_dormant', 'Pendiente de activar'),
    expired: t('vault.share.st_expired', 'Vencido'),
    consumed: t('vault.share.st_consumed', 'Usado'),
    revoked: t('vault.share.st_revoked', 'Revocado'),
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setCreated(null); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            {t('vault.share.title_dialog', 'Compartir por link temporal')}
          </DialogTitle>
          <DialogDescription>
            {t('vault.share.sub', 'El link lleva una copia de la credencial de este momento: una rotación posterior no lo afecta. Quien lo abre no necesita Internal. Cada apertura queda registrada con IP.')}
          </DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="space-y-3 rounded-md border bg-muted/40 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('vault.share.your_link', 'Tu link')}</div>
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded-md bg-background px-3 py-2 font-mono text-xs" data-tour="share-url">{created.url}</code>
              <Button variant="outline" size="icon" onClick={() => copy(created.url, t('vault.share.copied', 'Link copiado'))} title={t('vault.reveal.copy', 'Copiar')} aria-label={t('vault.reveal.copy', 'Copiar')} data-tour="share-copy">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('vault.share.created_note', 'Vence {{when}}. Mandalo por un canal directo: pegarlo en Slack no lo consume (el reveal es un click en la página), pero cualquiera con el link puede abrirlo.', { when: fmtRelative(created.expiresAt, i18n.language) })}
            </p>
            <Button variant="outline" size="sm" onClick={() => setCreated(null)}>{t('vault.share.another', 'Crear otro')}</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>{t('vault.share.expires', 'Vence en')}</Label>
                <Select value={hours} onValueChange={setHours}>
                  <SelectTrigger data-tour="share-expires"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">{t('vault.share.h1', '1 hora')}</SelectItem>
                    <SelectItem value="24">{t('vault.share.h24', '24 horas')}</SelectItem>
                    <SelectItem value={String(VAULT_POLICY.shareLinkMaxHours)}>{t('vault.share.h72', '{{h}} horas (máximo)', { h: VAULT_POLICY.shareLinkMaxHours })}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 pt-6">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={oneTime} onCheckedChange={(v) => setOneTime(v === true)} data-tour="share-onetime" />
                  {t('vault.share.one_time', 'Un solo uso')}
                </label>
                {canOwn && hasTotp && (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={includeTotp} onCheckedChange={(v) => setIncludeTotp(v === true)} />
                    {t('vault.share.include_totp', 'Incluir código MFA')}
                  </label>
                )}
              </div>
            </div>
            <Button onClick={submit} disabled={create.isPending} className="w-full" data-tour="share-create">
              {create.isPending ? t('vault.form.saving', 'Guardando…') : t('vault.share.create', 'Crear link')}
            </Button>
          </div>
        )}

        <div className="space-y-2 border-t pt-3" data-tour="share-existing">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('vault.share.existing', 'Links de esta credencial')}</div>
          {links.isLoading ? null : (links.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">{t('vault.share.none', 'Ninguno todavía.')}</p>
          ) : (
            <ul className="divide-y">
              {links.data!.map((l) => (
                <li key={l.linkId} className="flex items-center gap-2 py-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={STATUS_VARIANT[l.status as LinkStatus]}>{statusLabel[l.status as LinkStatus]}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {t('vault.share.meta', '{{who}} · {{when}} · {{views}} vistas{{one}}{{totp}}', {
                          who: l.createdBy.split('@')[0],
                          when: fmtRelative(l.createdAt, i18n.language),
                          views: l.viewCount,
                          one: l.maxViews === 1 ? ` · ${t('vault.share.one_time', 'Un solo uso').toLowerCase()}` : '',
                          totp: l.includesTotp ? ' · TOTP' : '',
                        })}
                      </span>
                    </div>
                    {l.lastViewedAt && (
                      <div className="truncate text-xs text-muted-foreground">
                        {t('vault.share.last_view', 'última vista {{when}} desde {{ip}}', { when: fmtRelative(l.lastViewedAt, i18n.language), ip: l.lastViewedIp ?? '?' })}
                      </div>
                    )}
                  </div>
                  {l.status === 'active' && (
                    <Button variant="ghost" size="icon" title={t('vault.share.revoke', 'Revocar')} aria-label={t('vault.share.revoke', 'Revocar')} onClick={() => revoke.mutate({ linkId: l.linkId })} disabled={revoke.isPending}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
